import _ from 'lodash'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { useLocation } from 'react-router'

import ScreenShotControl, { ScreenShotControlProps } from '@/components/chime-sdk/controls/ScreenShotControl'

import { useRequestHandler } from '@/hooks/useRequestHandler'

import { useAppSelector } from '@/redux/rootReducer'
import { selectConsultationById } from '@/redux/reducers/consultation.reducer'
import { addDocument, selectDocumentCreateRequestTracker } from '@/redux/reducers/document.reducer'

const SCREENSHOT_PREFIX = 'visio' // keep visio for backward compatibility in angular app

export default function ScreenShotControlContainer(props: ScreenShotControlProps) {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const { dispatch, requestTracker: documentCreateRequestTracker } = useRequestHandler({
    onSuccess: () => enqueueSnackbar(t('notification.meeting.captureScreenShot.success'), { variant: 'success' }),
    onError: () => enqueueSnackbar(t('notification.meeting.captureScreenShot.error'), { variant: 'error' }),
    requestTrackerSelector: selectDocumentCreateRequestTracker
  })
  const location = useLocation()
  const consultationId = (new URLSearchParams(location.search)).get('consultationId')

  const consultation = useAppSelector(state => consultationId ? selectConsultationById(state, consultationId) : undefined)

  const screenshotCount = useMemo(() => {
    if (!consultation) return 0

    return _.filter(
      consultation.files,
      (file) => file.filename.startsWith(SCREENSHOT_PREFIX) && _.some(['png', 'jpg', 'jpeg'], (ext) => file.filename.endsWith(ext))
    ).length
  }, [consultation])

  const handleCaptureScreenShot = async (blob: Blob) => {
    if (!consultationId) return
    const fileName = `${SCREENSHOT_PREFIX}-screenshot-${Date.now().toString()}.png`
    const documentFile = new File([blob], fileName, { type: 'image/png' })
    dispatch(addDocument({ documentFile, objectType: 'consultation', objectId: consultationId }))
  }

  return (
    <ScreenShotControl {...props} count={screenshotCount} disabled={documentCreateRequestTracker.loading} onCaptureScreenShot={handleCaptureScreenShot} />
  )
}